// =====================================================
// Swiss Finance OS - Authentication Context
// Secure multi-tenant authentication with Supabase
// =====================================================

import React, { useEffect, useState, useMemo, useCallback, type ReactNode } from 'react';
import { AuthError, type Session, type User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AuthContext,
  type AuthContextType,
  type SignUpData,
  type Profile,
  type Organization,
} from './auth-context';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // React Query for profile with caching and deduplication
  const { data: profile = null, isLoading: isProfileLoading, isFetching: isProfileFetching } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .limit(1);

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading profile:', error);
        return null;
      }

      return data?.[0] ?? null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  // React Query for organization with caching
  const { data: organization = null } = useQuery({
    queryKey: ['organization', profile?.organization_id],
    queryFn: async () => {
      if (!profile?.organization_id) return null;

      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', profile.organization_id)
        .limit(1);

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading organization:', error);
        return null;
      }

      return data?.[0] ?? null;
    },
    enabled: !!profile?.organization_id,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
    gcTime: 20 * 60 * 1000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const userId = user?.id ?? null;
  const organizationId = profile?.organization_id ?? null;

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    // Get initial session
    const initializeAuth = async () => {
      try {
        // CRITICAL FIX: Add timeout to getSession to prevent infinite hang
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('getSession timeout')), 5000)
        );

        const { data: { session: initialSession } } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]) as Awaited<typeof sessionPromise>;

        if (mounted) {
          setSession(initialSession);
          setUser(initialSession?.user || null);

          // React Query will handle profile loading automatically
          setLoading(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        console.log('Auth state changed:', event, session?.user?.email);

        setSession(session);
        setUser(session?.user || null);

        if (event === 'SIGNED_OUT') {
          // Clear all cached data
          queryClient.clear();
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  // Sign in
  const signIn = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      return { error };
    } catch (error) {
      return { error: error as AuthError };
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign up
  const signUp = useCallback(async (email: string, password: string, userData: SignUpData) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: userData.first_name,
            last_name: userData.last_name,
            role: userData.role || 'partner', // Save role to user_metadata for use during onboarding
          }
        }
      });

      if (error) return { error };

      // NOTE: Profile creation is deferred to onboarding when we have organization_id
      // organization_id is a required field, so we can't create the profile during signup
      // The .maybeSingle() queries throughout the app handle missing profiles gracefully
      console.log('Signup successful - profile will be created during onboarding with organization_id');

      return { error };
    } catch (error) {
      return { error: error as AuthError };
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    setLoading(true);
    try {
      // Clear onboarding flag on logout to prevent other users bypassing onboarding
      localStorage.removeItem('onboarding_completed');
      
      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset password
  const resetPassword = useCallback(async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  }, []);

  // Update profile
  const updateProfile = useCallback(async (updates: Partial<Profile>) => {
    if (!userId) return { error: new Error('No authenticated user') };

    try {
      const { data: updatedProfile, error } = await supabase
        .from('users')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId)
        .select('*')
        .maybeSingle();

      if (error) return { error: new Error(error.message) };

      if (updatedProfile) {
        queryClient.setQueryData<Profile | null>(['user-profile', userId], (current) => {
          if (!current) {
            return updatedProfile;
          }
          return { ...current, ...updatedProfile };
        });
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }, [queryClient, userId]);

  // Refresh profile
  const refreshProfile = useCallback(async () => {
    if (!userId) return;

    const refetches: Array<Promise<unknown>> = [
      queryClient.refetchQueries({ queryKey: ['user-profile', userId], type: 'active' }),
    ];

    if (organizationId) {
      refetches.push(
        queryClient.refetchQueries({ queryKey: ['organization', organizationId], type: 'active' })
      );
    }

    await Promise.all(refetches);
  }, [queryClient, userId, organizationId]);

  // Memoize context value to prevent unnecessary re-renders
  const value: AuthContextType = useMemo(
    () => ({
      user,
      profile,
      organization,
      session,
      // Only use initial auth loading, not profile loading to prevent infinite loops
      loading: loading,
      isProfileLoading: isProfileLoading || isProfileFetching,
      signIn,
      signUp,
      signOut,
      resetPassword,
      updateProfile,
      refreshProfile,
    }),
    [user, profile, organization, session, loading, isProfileLoading, isProfileFetching, signIn, signUp, signOut, resetPassword, updateProfile, refreshProfile]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
